import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { setAgentFilterSearchParam } from "@/pages/agent/utils/agent-search-params.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";

export const ALL_PROJECTS_FILTER = "all-projects";
export const ALL_STATUSES_FILTER = "all-statuses";

export const TaskSortKey = {
  DueDate: "due-date",
  RecentlyUpdated: "recently-updated",
} as const;

export type TaskSortKey = (typeof TaskSortKey)[keyof typeof TaskSortKey];

export const DEFAULT_TASK_SORT: TaskSortKey = TaskSortKey.DueDate;

const TASK_PROJECT_ID_SEARCH_PARAM = "taskProjectId";
const TASK_STATUS_SEARCH_PARAM = "taskStatus";
const TASK_SEARCH_QUERY_SEARCH_PARAM = "taskSearch";
const TASK_SORT_SEARCH_PARAM = "taskSort";

const isTaskSortKey = (value: string): value is TaskSortKey =>
  value === TaskSortKey.DueDate || value === TaskSortKey.RecentlyUpdated;

export const useAgentTasksSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const projectFilter = searchParams.get(TASK_PROJECT_ID_SEARCH_PARAM) ?? ALL_PROJECTS_FILTER;
  const statusFilter = normalizeStatusValue({
    status: searchParams.get(TASK_STATUS_SEARCH_PARAM) ?? ALL_STATUSES_FILTER,
  });
  const searchQuery = searchParams.get(TASK_SEARCH_QUERY_SEARCH_PARAM) ?? "";
  const rawSortFilter = searchParams.get(TASK_SORT_SEARCH_PARAM) ?? DEFAULT_TASK_SORT;
  const sortFilter: TaskSortKey = isTaskSortKey(rawSortFilter) ? rawSortFilter : DEFAULT_TASK_SORT;

  const updateTaskFilters = useCallback(
    ({
      nextProjectFilter,
      nextStatusFilter,
      nextSearchQuery,
      nextSortFilter,
    }: {
      nextProjectFilter?: string;
      nextStatusFilter?: string;
      nextSearchQuery?: string;
      nextSortFilter?: string;
    }) => {
      setSearchParams(
        previousSearchParams => {
          const nextSearchParams = new URLSearchParams(previousSearchParams);

          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: TASK_PROJECT_ID_SEARCH_PARAM,
            value: nextProjectFilter,
            defaultValue: ALL_PROJECTS_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: TASK_STATUS_SEARCH_PARAM,
            value: nextStatusFilter,
            defaultValue: ALL_STATUSES_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: TASK_SEARCH_QUERY_SEARCH_PARAM,
            value: nextSearchQuery,
            defaultValue: "",
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: TASK_SORT_SEARCH_PARAM,
            value: nextSortFilter,
            defaultValue: DEFAULT_TASK_SORT,
          });

          return nextSearchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return {
    projectFilter,
    statusFilter,
    searchQuery,
    sortFilter,
    updateTaskFilters,
  };
};
